import React, { useState, useEffect, ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DataTable } from "capitalrx-components";
import { checkDate } from "capitalrx-utilities";
import { useNavigate } from "react-router";
import { useModulePathResolver } from "capitalrx-shell";

import { RootState } from "@/App/reducers";

import { getBatchManualClaimListActions } from "@/App/actions";
import { GeneralTypes } from "@/types";

const INITIAL_QUERY_OPTIONS = {
  order_by: [{ created_at: GeneralTypes.SortOrder.Desc }],
  page_number: 1,
  results_per_page: 20,
};

const ResultsTable: React.FC = () => {
  const navigate = useNavigate();
  const modulePath = useModulePathResolver();
  const { batchManualClaimListResults } = useSelector((state: RootState) => state.batchManualClaimList);
  const { results, result_count } = batchManualClaimListResults;

  const [initialQuery, setInitialQuery] = useState<GeneralTypes.QueryOptions>(INITIAL_QUERY_OPTIONS);

  const { getManualClaimList } = getBatchManualClaimListActions;

  const loading = result_count === "0";
  const dispatch = useDispatch();

  const handleUpdate = (queryOptions: GeneralTypes.QueryOptions): void => {
    setInitialQuery(queryOptions);
    dispatch(getManualClaimList({ queryOptions: queryOptions }));
  };

  useEffect(() => {
    handleUpdate(initialQuery);
  }, []);

  const tableOnClick = (e: { id: string }): void => {
    navigate(modulePath(`/manual-claim/batch/view/${e.id}`));
  };

  const tableHeaders = [
    {
      key: "created_at",
      label: "Uploaded On",
      formatter: (value: string): string => {
        return checkDate(value);
      },
    },
    {
      key: "original_name",
      label: "File Name",
    },
    {
      key: "type",
      label: "Type",
      formatter: (label: string): ReactNode => {
        let component = <span>{label}</span>;

        if (label === "final") {
          component = <span className="caprx-icon-successcheck">FINAL</span>;
        }

        return component;
      },
    },

    {
      key: "status",
      label: "Status",
      formatter: (label: string): ReactNode => {
        let component = <span>Uploaded</span>;

        if (label === "processed") {
          component = <span className="caprx-icon-successcheck">Processed</span>;
        }

        if (label === "processing") {
          component = <span className="caprx-icon-dots">Processing</span>;
        }

        return component;
      },
    },
  ];

  const resultHeader = results && `${result_count} batch claim(s) found:`;

  return (
    <div className="table-section-container-wrapper">
      <div className="table-section-result-header">{resultHeader}</div>
      <DataTable
        data={results}
        handleUpdateRequest={handleUpdate}
        resultsTotalCount={result_count}
        headers={tableHeaders}
        searchIsEnabled={false}
        queryOptions={initialQuery}
        isLoading={loading}
        rowIsClickable
        ableToSort={false}
        onClick={tableOnClick}
      />
    </div>
  );
};

export default ResultsTable;
